<template>
  <Combobox v-model="selectedOptionLocal" as="div">
    <ComboboxLabel class="block text-sm/6 font-medium text-gray-900">
      <slot name="label" />
    </ComboboxLabel>
    <div class="relative">
      <ComboboxInput class="block w-full rounded-lg border-0 text-gray-900 ring-1 ring-inset placeholder:text-gray-400 focus:ring-1 focus:ring-inset focus:ring-gray-800 sm:leading-6 py-2 min-h-[44px] ring-gray-300 hover:ring-gray-400" :display-value="(option) => rowFieldValue(option, labelKey)" @change="query = $event.target.value" @blur="query = ''" />
      <ComboboxButton class="absolute inset-y-0 right-0 flex items-center rounded-r-md px-2 focus:outline-none">
        <ChevronDownIcon class="size-5 text-gray-400" aria-hidden="true" />
      </ComboboxButton>
      <ComboboxOptions v-if="(options && options.length > 0) || (query && query.length > 0)" class="absolute z-10 mt-1 max-h-56 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black/5 focus:outline-none sm:text-sm">
        <ComboboxOption v-for="option in options" :key="option.id" v-slot="{ active }" :value="option" as="template">
          <li :class="['relative cursor-default select-none px-3 py-2', active ? 'bg-gray-50 outline-none' : 'text-gray-900']">
            <div class="flex items-center">
              <tw-img v-if="avatarKey && rowFieldValue(option, avatarKey)" :src="rowFieldValue(option, avatarKey)" alt="" class="size-8 shrink-0 rounded-full" />
              <span v-else class="inline-flex h-8 w-8 items-center justify-center rounded-full bg-gray-100">
                <span v-if="option.cached && option.cached.owner_initials" class="text-xs font-sans leading-none text-gray-400">{{ option.cached.owner_initials }} </span>
              </span>

              <span class="ml-3 truncate">
                {{ labelKey? rowFieldValue(option, labelKey): option }}
              </span>
            </div>
          </li>
        </ComboboxOption>
      </ComboboxOptions>
      <div v-if="options.length === 0 && query !== '' && query !== null" class="relative cursor-default select-none px-4 py-2 text-gray-700">
        Nothing found.
      </div>
    </div>
  </Combobox>
</template>

<script setup>
import { ref, computed, toRef, watch } from 'vue'
import { ChevronDownIcon } from '@heroicons/vue/20/solid'

import { UserIcon } from '@heroicons/vue/16/solid'
import { rowFieldValue } from '@/mixins/lists/rowFieldValueNested.js'

import {
  Combobox,
  ComboboxButton,
  ComboboxInput,
  ComboboxLabel,
  ComboboxOption,
  ComboboxOptions,
} from '@headlessui/vue'
import TwImg from '@/components/images/tw-img.vue'

// Props: query and selected option both passed in via v-model bindings
const props = defineProps({
  modelValue: {
    type: String,
    default: null
  },
  selectedOption: {
    type: Object,
    default: null
  },
  options: {
    type: Array,
    required: true
  },
  placeholder: {
    type: String,
    default: 'Search...'
  },
  avatarKey: {
    type: String,
    default: null
  },
  labelKey: {
    type: String,
    default: null
  }
})

// Emits: for both v-model bindings
const emit = defineEmits([
  'update:modelValue',          // for query
  'update:selectedOption'       // for selectedOption
])

// Refs for the incoming props
const query = ref(props.modelValue)
watch(() => props.modelValue, (newVal) => {
  if (newVal !== query.value) query.value = newVal
})
watch(query, (newVal) => {
  emit('update:modelValue', newVal)
})

// ✅ Local reactive copy of selectedOption
const selectedOptionLocal = ref(props.selectedOption)


watch(() => props.selectedOption, (newVal) => {

  if (newVal !== selectedOptionLocal.value) selectedOptionLocal.value = newVal
})
watch(selectedOptionLocal, (newVal) => {
  emit('update:selectedOption', newVal)
})

// No filtering done — options are passed in filtered
const options = computed(() => props.options)

// Optional: create a pseudo-option from current query
const queryOption = computed(() => {
  return query.value === '' ? null : { id: null, name: query.value }
})
</script>
